import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckDouble, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  bulkImportService,
  type BulkImportSession,
  type BulkImportRowListItem,
  type BulkImportRowClassification,
} from '@/services/bulk-import.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { generateUuid } from '@/lib/uuid';
import { BulkImportCommitConfirmDialog } from './BulkImportCommitConfirmDialog';
import { BulkImportFinalizeDialog } from './BulkImportFinalizeDialog';
import { BulkImportResultLinks } from './BulkImportResultLinks';
import { receivingLotService } from '@/services/receiving-lot.service';

const FETCH_PAGE_SIZE = 100;

/** Fetches every row of one classification for this session (bounded by the
 * existing 1,000-row session cap — at most 10 requests of 100 each) and
 * filters out already-committed/abandoned rows. Used both for the Warning
 * acknowledgement list (classification-scoped) and, for a
 * `PARTIALLY_COMPLETED` session, to compute an exact remaining-unresolved
 * count for the Finalize dialog — no backend contract change: the row list
 * endpoint already exposes `committed`/`abandoned` per row, this just reads
 * more of it than the paginated main table does. */
async function fetchUnresolvedRows(
  sessionId: string,
  classification: BulkImportRowClassification,
): Promise<BulkImportRowListItem[]> {
  const all: BulkImportRowListItem[] = [];
  let page = 1;
  for (;;) {
    const result = await bulkImportService.listRows(sessionId, {
      page,
      pageSize: FETCH_PAGE_SIZE,
      classification,
    });
    all.push(...result.data);
    if (result.data.length < FETCH_PAGE_SIZE || all.length >= result.total) break;
    page += 1;
  }
  return all.filter((r) => !r.committed && !r.abandoned);
}

interface BulkImportFinalImportSectionProps {
  session: BulkImportSession;
  onSessionChanged: () => void;
}

/**
 * Phase 2f-F: activates the existing Phase 2f backend commit/finalize
 * engine in the UI. Rendered inside the review stage for both
 * `REVIEW_REQUIRED` and `PARTIALLY_COMPLETED` — the backend's own
 * `COMMITTABLE_STATUSES` set (bulk-import-commit.service.ts) is exactly
 * these two, never re-derived independently here.
 */
export function BulkImportFinalImportSection({
  session,
  onSessionChanged,
}: BulkImportFinalImportSectionProps) {
  const queryClient = useQueryClient();
  const can = useHasPermission();

  const [ackWarningIds, setAckWarningIds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  // Minted once per "logical commit attempt" (component mount / after a
  // confirmed backend response) — never regenerated merely because the
  // user double-clicked, so a retry after a network timeout reuses the
  // exact same key (mirrors AddArrivalDialog.tsx's established pattern).
  const [commitIdempotencyKey, setCommitIdempotencyKey] = useState(() => generateUuid());

  const canCommit =
    session.status === 'REVIEW_REQUIRED' || session.status === 'PARTIALLY_COMPLETED';
  const canFinalize = session.status === 'PARTIALLY_COMPLETED' && can('bulk-import:finalize');

  const { data: warningRows, isLoading: warningsLoading } = useQuery({
    queryKey: [
      'bulk-import-unresolved-warnings',
      session.id,
      session.warningCount,
      session.updatedAt,
    ],
    queryFn: () => fetchUnresolvedRows(session.id, 'WARNING'),
    enabled: canCommit && session.warningCount > 0,
  });

  // Only fetched for a PARTIALLY_COMPLETED session, and only when the
  // Finalize dialog is actually reachable — an exact "still unresolved"
  // PASS_EXISTING/PASS_NEW count the session summary alone cannot provide
  // (its classification counters are never filtered by committed status).
  const { data: unresolvedPassExisting } = useQuery({
    queryKey: ['bulk-import-unresolved-pass-existing', session.id, session.updatedAt],
    queryFn: () => fetchUnresolvedRows(session.id, 'PASS_EXISTING'),
    enabled: canFinalize,
  });
  const { data: unresolvedPassNew } = useQuery({
    queryKey: ['bulk-import-unresolved-pass-new', session.id, session.updatedAt],
    queryFn: () => fetchUnresolvedRows(session.id, 'PASS_NEW'),
    enabled: canFinalize,
  });

  const { data: targetLot } = useQuery({
    queryKey: ['receiving-lot', session.targetReceivingLotId],
    queryFn: () => receivingLotService.get(session.targetReceivingLotId!),
    enabled: session.targetMode === 'EXISTING_LOT' && !!session.targetReceivingLotId,
  });

  // Ephemeral acknowledgement is scoped to a specific, currently-visible
  // warning-row set — reset whenever the session materially changes
  // (`updatedAt` moves on every correction/revalidation/commit/finalize),
  // never carried forward as a stale selection that could reference a row
  // no longer eligible.
  useEffect(() => {
    setAckWarningIds(new Set());
  }, [session.id, session.status, session.updatedAt]);

  const unresolvedWarnings = useMemo(() => warningRows ?? [], [warningRows]);
  const acknowledgedCount = ackWarningIds.size;
  const excludedUnacknowledgedWarnings = unresolvedWarnings.length - acknowledgedCount;

  const toggleWarning = (id: string) =>
    setAckWarningIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAllWarnings = () => setAckWarningIds(new Set(unresolvedWarnings.map((r) => r.id)));
  const clearAllWarnings = () => setAckWarningIds(new Set());

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['bulk-import-session', session.id] });
    void queryClient.invalidateQueries({ queryKey: ['bulk-import-rows', session.id] });
    void queryClient.invalidateQueries({
      queryKey: ['bulk-import-unresolved-warnings', session.id],
    });
    void queryClient.invalidateQueries({
      queryKey: ['bulk-import-unresolved-pass-existing', session.id],
    });
    void queryClient.invalidateQueries({
      queryKey: ['bulk-import-unresolved-pass-new', session.id],
    });
  };

  const commitMutation = useMutation({
    mutationFn: () =>
      bulkImportService.commit(session.id, {
        commitIdempotencyKey,
        acknowledgeWarnings: Array.from(ackWarningIds),
      }),
    onSuccess: () => {
      toast.success('Final Import started — this may take a few moments.');
      setConfirmOpen(false);
      setAckWarningIds(new Set());
      // A confirmed backend response was received for this attempt — safe
      // to mint a fresh key for the next logical attempt. Never done on
      // error, so a retry after a timeout reuses this same key.
      setCommitIdempotencyKey(generateUuid());
      invalidateAll();
      onSessionChanged();
    },
    onError: (e: Error) => {
      setConfirmOpen(false);
      toast.error(getApiErrorMessage(e, 'Failed to start Final Import'));
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: () => bulkImportService.finalize(session.id),
    onSuccess: () => {
      toast.success('Session finalized — remaining rows abandoned');
      setFinalizeOpen(false);
      invalidateAll();
      onSessionChanged();
    },
    onError: (e: Error) => {
      setFinalizeOpen(false);
      toast.error(getApiErrorMessage(e, 'Failed to finalize session'));
    },
  });

  const counts = useMemo(
    () => ({
      passExisting: session.passExistingCount,
      passNew: session.passNewCount,
      acknowledgedWarnings: acknowledgedCount,
      excludedUnacknowledgedWarnings,
      excludedFail: session.failCount,
      excludedConflict: session.conflictCount,
      totalConsidered: session.totalRows,
    }),
    [session, acknowledgedCount, excludedUnacknowledgedWarnings],
  );

  const eligibleCount = counts.passExisting + counts.passNew + counts.acknowledgedWarnings;

  const finalizeCounts = useMemo(() => {
    const unresolvedPass = (unresolvedPassExisting?.length ?? 0) + (unresolvedPassNew?.length ?? 0);
    return {
      unresolved:
        unresolvedPass + unresolvedWarnings.length + session.failCount + session.conflictCount,
      fail: session.failCount,
      conflict: session.conflictCount,
      warning: unresolvedWarnings.length,
    };
  }, [unresolvedPassExisting, unresolvedPassNew, unresolvedWarnings, session]);

  return (
    <div className="space-y-5 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Final Import</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {eligibleCount} of {session.totalRows} row(s) are currently eligible to import
            {counts.acknowledgedWarnings > 0
              ? ` (including ${counts.acknowledgedWarnings} acknowledged warning row(s))`
              : ''}
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canFinalize && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFinalizeOpen(true)}
              disabled={finalizeMutation.isPending}
            >
              Finalize — Abandon Remaining
            </Button>
          )}
          <Button
            size="sm"
            disabled={!canCommit || eligibleCount === 0 || commitMutation.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {commitMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Final Import
          </Button>
        </div>
      </div>

      {eligibleCount === 0 && canCommit && (
        <p className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          No rows are currently eligible to import. Correct and revalidate Fail/Conflict rows above,
          or acknowledge Warning rows below, to make Final Import available.
        </p>
      )}

      {session.warningCount > 0 && (
        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Unresolved Warning Rows ({unresolvedWarnings.length})
            </p>
            {unresolvedWarnings.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllWarnings}
                  disabled={!canCommit || acknowledgedCount === unresolvedWarnings.length}
                >
                  <FontAwesomeIcon icon={faCheckDouble} className="mr-1.5 h-3 w-3" />
                  Select All Warnings
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllWarnings}
                  disabled={!canCommit || acknowledgedCount === 0}
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="mr-1.5 h-3 w-3" />
                  Clear
                </Button>
              </div>
            )}
          </div>

          {warningsLoading ? (
            <div className="flex justify-center py-6">
              <FontAwesomeIcon
                icon={faSpinner}
                className="h-5 w-5 animate-spin text-muted-foreground"
              />
            </div>
          ) : unresolvedWarnings.length === 0 ? (
            <EmptyState title="No unresolved Warning rows" />
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-border">
                  {unresolvedWarnings.map((row) => (
                    <tr key={row.id} className="hover:bg-muted/30">
                      <td className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Acknowledge warning for row ${row.rowNumber}`}
                          checked={ackWarningIds.has(row.id)}
                          onChange={() => toggleWarning(row.id)}
                          disabled={!canCommit}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        Row {row.rowNumber}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {row.serialNumber ?? row.assetNumber ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {row.equipmentName ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Acknowledged warning rows will be included in this Final Import attempt. Fail and
            Conflict rows always remain excluded, regardless of selection here.
          </p>
        </div>
      )}

      {session.status === 'PARTIALLY_COMPLETED' && (
        <div className="space-y-2 border-t border-border pt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Prior Import Result
          </p>
          <BulkImportResultLinks
            resultingReceivingLotId={session.resultingReceivingLotId}
            resultingArrivalId={session.resultingArrivalId}
          />
        </div>
      )}

      <BulkImportCommitConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => commitMutation.mutate()}
        isPending={commitMutation.isPending}
        counts={counts}
        targetMode={session.targetMode}
        targetLotNumber={targetLot?.lotNumber}
      />

      <BulkImportFinalizeDialog
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        onConfirm={() => finalizeMutation.mutate()}
        isPending={finalizeMutation.isPending}
        counts={finalizeCounts}
      />
    </div>
  );
}
